import { useEffect, useState } from "react"

import { Loader2 } from "lucide-react"

import { Markdown } from "@/components/chat/Markdown"
import { summarizeSource } from "@/lib/api"
import { parseSSEStream } from "@/lib/sse"
import type { PreviewDoc } from "@/providers/PreviewProvider"
import type { SummaryStreamEvent } from "@/types/api"

/** Streams an AI summary of the previewed document. Mounted lazily on first tab activation. */
export function SummaryTab({ doc }: { doc: PreviewDoc }) {
  const [text, setText] = useState("")
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false
    setText("")
    setFailed(false)

    void (async () => {
      try {
        const response = await summarizeSource(
          { sourceUrl: doc.url, driveId: doc.driveId, itemId: doc.itemId },
          controller.signal,
        )
        for await (const event of parseSSEStream<SummaryStreamEvent>(response)) {
          if (cancelled) break
          if (event.type === "token") setText((t) => t + event.content)
          else if (event.type === "error") setFailed(true)
        }
      } catch (error) {
        if (!cancelled && (error as Error).name !== "AbortError") setFailed(true)
      }
    })()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [doc])

  if (failed) {
    return <p className="p-4 text-sm text-muted-foreground">Could not load summary.</p>
  }
  if (!text) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" />
        <span className="text-shimmer">Generating summary…</span>
      </div>
    )
  }
  return (
    <div className="overflow-y-auto p-4">
      <Markdown content={text} />
    </div>
  )
}
